import { ActionTable } from '@/components';
import {
  FormComboBox,
  FormController,
  FormLabel,
  FormTextArea,
} from '@/components/form';
import { FrameDetailsFeatures } from '@/features';
import { useAppSelector } from '@/state';
import { ProductionApi, UsersApi } from '@/state/api';
import { ProductionType } from '@/types';
import { Flex, Span, Stack, Text, VStack } from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Card,
  DataList,
  OverlayDialog,
  Table,
  TableColumnsConfigType,
  toasterApi,
} from '@ui';
import { useRef, useState } from 'react';
import z from 'zod';

const Create = () => {
  const formResetRef = useRef<(() => void) | null>(null);
  const [updated, setUpdated] = useState(0);
  const { selection } = ActionTable.useSelection();

  const [selectedFrame, setSelectedFrame] =
    useState<WorkOrderFramesType | null>(null);

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );

  const { data: workOrderFrames, isLoading: isWorkOrderFramesLoading } =
    ProductionApi.Api.useGetWorkOrderFramesQuery(
      selectedProject?.id && selectedRelease?.id
        ? {
            params: {
              project_id: selectedProject.id,
              release_id: selectedRelease.id,
            },
          }
        : skipToken,
    );

  const { data: getAllUserNames } = UsersApi.Api.useGetAllUserNamesQuery();

  const [postWorkOrder, { isLoading: isPostWorkOrderLoading }] =
    ProductionApi.Api.usePostWorkOrderMutation();

  const onFormSubmit = async (formData: FormData) => {
    OverlayDialog.open('confirmWorkOrderCreate', {
      overlayId: 'confirmWorkOrderCreate',
      title: 'Confirm Work Order Create',
      description: `Are you sure you want to create the work order/s?`,
      content: (
        <VStack align="flex-start" gap={2}>
          <Text color="tertiary.fg">Frame Orders:</Text>
          <DataList
            items={[
              {
                label: 'Project',
                value: selectedProject.item?.name,
              },
              {
                label: 'Release',
                value: selectedRelease.item?.number,
              },
              {
                label: 'Assigned Users',
                value: (
                  <Span color="fg">
                    {formData.assigned_users
                      .map(
                        (userId) =>
                          getAllUserNames?.find((u) => u.id === userId)
                            ?.display_name,
                      )
                      .join(', ')}
                  </Span>
                ),
              },
              {
                label: 'Total Work Orders to Create',
                value: `${selection?.getArray().length}`,
              },
            ]}
            orientation="horizontal"
            divideY="1px"
          />
          <Table
            columns={[
              { name: { label: 'Frame', width: '200px' } },
              { quantity: { label: 'Quantity', width: '100px' } },
            ]}
            items={selection?.map((frame) => ({
              id: frame?.id,
              name: frame?.name,
              quantity: frame?.quantity,
            }))}
          />
        </VStack>
      ),
      handleConfirm: () => {
        if (!selectedProject?.id || !selectedRelease?.id) return;

        const payload: ProductionType.WorkOrders.Post['body'] = {
          frames: selection?.map((frame) => ({
            frame: frame?.id,
            description: formData?.description,
          })),
          assigned_users: formData?.assigned_users,
        };

        postWorkOrder({
          body: payload,
          params: {
            project_id: selectedProject?.id,
            release_id: selectedRelease?.id,
          },
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 201,
              code: 'Success',
              message: 'Work Order',
              detail: 'Work Order created successfully.',
            });

            formResetRef.current?.();
            setUpdated((prev) => prev + 1);
            selection.clear();
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Work Order',
              detail: 'Could not create work order.',
            });
          })
          .finally(() => {
            OverlayDialog.close('confirmWorkOrderCreate');
          });
      },
    });
  };

  const handleRowClick = (row: WorkOrderFramesType) => {
    setSelectedFrame(row);
  };

  const disableForm =
    !selectedProject.id || !selectedRelease.id || selection.size <= 0;

  const renderCell = (
    props: ActionTable.RenderCellType<WorkOrderFramesType>,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'id':
        return (
          <Text
            aria-label="view-frame-details"
            whiteSpace="nowrap"
            color="tertiary.solid"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(rowValue);
            }}
            cursor="pointer"
            textDecoration="underline"
            textUnderlineOffset="2px"
          >
            {cellValue.toString()}
          </Text>
        );
      case 'name':
        return (
          <Text
            whiteSpace="nowrap"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(rowValue);
            }}
            cursor="pointer"
          >
            {cellValue}
          </Text>
        );
      default:
        return cellValue;
    }
  };

  return (
    <Flex flexDirection="column">
      <Flex gapX={4}>
        <Flex flex={1}>
          <Card>
            <Stack
              flexDirection={{
                base: 'column',
                xl: 'row',
              }}
            >
              <FormController
                key={`create-work-order-${isPostWorkOrderLoading}`}
                label="Create Work Order"
                formSchema={workOrderFormSchema}
                helperText={
                  disableForm
                    ? 'Please select at least one frame to create a work order.'
                    : 'Please fill out the form to create a new work order.'
                }
                onFormSubmit={onFormSubmit}
                disabled={!selectedProject.id || !selectedRelease.id}
                disableInputs={disableForm}
                disableLabels={disableForm}
                disableButton={disableForm}
                defaultValues={{
                  assigned_users: [],
                  description: '',
                }}
                buttonProps={{
                  variant: 'outline',
                  color: 'tertiary.fg',
                  width: '100%',
                  mt: '4',
                  'data-testid': 'tutorial-create-workorder-submit',
                }}
              >
                {({
                  register,
                  control,
                  errors,
                  reset,
                  disabled,
                  disableLabel,
                }) => {
                  formResetRef.current = () =>
                    reset?.({
                      assigned_users: [],
                      description: '',
                    });

                  return (
                    <>
                      <ActionTable.FilterBar
                        filterConfigComponents={filterConfigComponents}
                        disabled={!selectedProject.id || !selectedRelease.id}
                      />

                      <FormLabel
                        label="Frames"
                        labelContainerFlexDirection="column"
                        helperText="Select a frame to preview its parts and drawing on the right"
                      >
                        <ActionTable
                          columns={workOrderFramesTableColumns}
                          disabled={!selectedProject.id || !selectedRelease.id}
                          isLoading={isWorkOrderFramesLoading}
                          items={workOrderFrames ?? []}
                          columnToUseAsRowKey="id"
                          isFilterable={true}
                          isCheckbox={true}
                          isSortable={true}
                          isShowActionBar={true}
                          onRowClick={handleRowClick}
                          height="45vh"
                          renderCell={renderCell}
                        />
                      </FormLabel>

                      <FormComboBox
                        key={`assigned_users-${updated}`}
                        register={register}
                        control={control}
                        errors={errors}
                        id="assigned_users"
                        label="Assigned users"
                        keyForDisplay="display_name"
                        keyForValue="id"
                        items={getAllUserNames ?? []}
                        showLabelAtTop
                        multiple
                        useSelectAllOption
                        clearable
                        disabled={disabled}
                        disableLabel={disableLabel}
                        data-testid="tutorial-workorder-assignee-select"
                      />

                      <FormTextArea
                        key={`description-${updated}`}
                        register={register}
                        control={control}
                        errors={errors}
                        id="description"
                        label="Description"
                        showLabelAtTop
                        disabled={disabled}
                        disableLabel={disableLabel}
                      />
                    </>
                  );
                }}
              </FormController>
            </Stack>
          </Card>
        </Flex>

        <Flex flex={1}>
          <FrameDetailsFeatures.FrameDetailsInfo
            frameId={selectedFrame?.id}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default () => {
  return (
    <ActionTable.Provider>
      <Create />
    </ActionTable.Provider>
  );
};

type WorkOrderFramesType =
  ProductionType.WorkOrderFrames.GetFrames['response'][number];

const workOrderFramesTableColumns: TableColumnsConfigType<WorkOrderFramesType> =
  [
    {
      id: {
        label: 'Id',
        width: '100px',
      },
    },
    {
      name: {
        label: 'Name',
        width: '200px',
      },
    },
    {
      quantity: {
        label: 'Quantity',
        width: '200px',
      },
    },
  ];

const workOrderFormSchema = z.object({
  assigned_users: z.array(z.number()).min(1, {
    message: 'Please select at least one user',
  }),
  description: z.string().optional(),
});

type FormData = z.infer<typeof workOrderFormSchema>;

const filterConfigComponents: ActionTable.FilterConfigComponentType<WorkOrderFramesType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Frame',
      filterConfigItems: [
        {
          colKeys: ['name'],
          value: '',
          operator: 'contains',
        },
      ],
    },
  ];